import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FrameCropEditor } from "./FrameCropEditor";
import { RecolorAnimationModal } from "./RecolorAnimationModal";
import { CLIP_POSES, CLIP_META, CLIP_FRAME_COUNTS, ATTACK_TYPES } from "../../lib/animationPoses";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Download, Sparkles, Wand2, Palette } from "lucide-react";

const OPTIONAL_CLIPS = ["idle", "walk", "hit", "death", "resurrection"];
let blockSeq = 0;

// Full Animation Maker: build a new animated token (idle/walk/hit/death/resurrection + any
// number of tagged Attack Variants) OR pick/apply/delete one already in the GM's library.
// If `applyToTokenId` is set, saving/applying auto-assigns the result to that token and closes;
// otherwise this is pure library management (opened from the GM Sidebar's Assets tab).
export default function TokenAnimationMakerModal({ open, onOpenChange, gameId, applyToTokenId }) {
  const [tab, setTab] = useState("create");
  const [name, setName] = useState("");
  const [enabled, setEnabled] = useState({ idle: true, walk: true, hit: false, death: false, resurrection: false });
  const [frames, setFrames] = useState({}); // { [clip]: [{blob,url}, ...] }
  const [fps, setFps] = useState({});
  const [attackBlocks, setAttackBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [library, setLibrary] = useState([]);
  const [downloadClip, setDownloadClip] = useState({}); // { [animId]: clipKey }
  const [recolorTarget, setRecolorTarget] = useState(null); // the anim object being recolored, or null

  const loadLibrary = () => api.get("/animations/mine").then((r) => setLibrary(r.data)).catch(() => {});

  useEffect(() => {
    if (open) loadLibrary();
  }, [open]);

  const setFrame = (clip, idx, blob, url) => {
    setFrames((f) => {
      const arr = [...(f[clip] || Array(CLIP_FRAME_COUNTS[clip]).fill(null))];
      arr[idx] = blob ? { blob, url } : null;
      return { ...f, [clip]: arr };
    });
  };

  const addAttackBlock = () => {
    if (attackBlocks.length >= 5) return toast.error("That's already 5 Attack Variants — plenty to work with");
    blockSeq += 1;
    setAttackBlocks((b) => [...b, { id: blockSeq, types: [], frames: Array(3).fill(null), fps: CLIP_META.attack.defaultFps }]);
  };

  const removeAttackBlock = (id) => setAttackBlocks((b) => b.filter((blk) => blk.id !== id));

  const toggleAttackType = (blockId, type) => {
    setAttackBlocks((b) =>
      b.map((blk) => {
        if (blk.id !== blockId) return blk;
        const has = blk.types.includes(type);
        return { ...blk, types: has ? blk.types.filter((t) => t !== type) : [...blk.types, type] };
      })
    );
  };

  const setAttackFrame = (blockId, idx, blob, url) => {
    setAttackBlocks((b) =>
      b.map((blk) => {
        if (blk.id !== blockId) return blk;
        const arr = [...blk.frames];
        arr[idx] = blob ? { blob, url } : null;
        return { ...blk, frames: arr };
      })
    );
  };

  const resetForm = () => {
    setName("");
    setEnabled({ idle: true, walk: true, hit: false, death: false, resurrection: false });
    setFrames({});
    setFps({});
    setAttackBlocks([]);
  };

  const buildFormData = () => {
    const fd = new FormData();
    const clipNames = [];
    const fpsList = [];
    const loopList = [];
    const countList = [];
    const allFiles = [];

    OPTIONAL_CLIPS.forEach((clip) => {
      if (!enabled[clip]) return;
      const clipFrames = frames[clip] || [];
      const need = CLIP_FRAME_COUNTS[clip];
      if (clipFrames.filter(Boolean).length < need) return;
      clipNames.push(clip);
      fpsList.push(fps[clip] || CLIP_META[clip].defaultFps);
      loopList.push(CLIP_META[clip].loop);
      countList.push(need);
      clipFrames.slice(0, need).forEach((f) => allFiles.push(f.blob));
    });

    attackBlocks.forEach((blk) => {
      if (blk.frames.filter(Boolean).length < 3) return;
      const keys = blk.types.length ? blk.types.map((t) => `attack_${t}`) : ["attack"];
      keys.forEach((key) => {
        clipNames.push(key);
        fpsList.push(blk.fps || CLIP_META.attack.defaultFps);
        loopList.push(CLIP_META.attack.loop);
        countList.push(3);
        blk.frames.slice(0, 3).forEach((f) => allFiles.push(f.blob));
      });
    });

    if (!clipNames.length) return null;
    fd.append("name", name.trim() || "Animation");
    fd.append("clip_names", clipNames.join(","));
    fd.append("fps", fpsList.join(","));
    fd.append("loop", loopList.join(","));
    fd.append("frames_per_clip", countList.join(","));
    allFiles.forEach((blob, i) => fd.append("files", blob, `frame_${i}.png`));
    return { fd, clipCount: clipNames.length };
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Give this animation a name");
    const built = buildFormData();
    if (!built) return toast.error("Finish at least one full clip (every frame slot filled) before saving");
    setSaving(true);
    try {
      const { data } = await api.post("/animations", built.fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`"${data.name}" saved with ${built.clipCount} clip${built.clipCount > 1 ? "s" : ""}`);
      if (applyToTokenId) {
        await api.post(`/games/${gameId}/tokens/${applyToTokenId}/apply-animation`, { animation_id: data.id });
        toast.success("Applied to token");
        onOpenChange(false);
      } else {
        resetForm();
        loadLibrary();
        setTab("library");
      }
    } catch (err) {
      if (err.response?.status === 403) toast.error("The Animation Maker needs an active GM Pass subscription");
      else toast.error(err.response?.data?.detail || "Could not save this animation");
    } finally {
      setSaving(false);
    }
  };

  const applyExisting = async (animId) => {
    try {
      await api.post(`/games/${gameId}/tokens/${applyToTokenId}/apply-animation`, { animation_id: animId });
      toast.success("Applied to token");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not apply this animation");
    }
  };

  const deleteAnimation = async (animId) => {
    await api.delete(`/animations/${animId}`);
    loadLibrary();
  };

  const downloadGif = (animId) => {
    const clip = downloadClip[animId];
    if (!clip) return toast.error("Pick a clip to download first");
    window.open(`${API}/animations/${animId}/export.gif?clip=${clip}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#151613] border-white/10 text-[#f1e0c5] max-w-3xl p-0 overflow-hidden" data-testid="animation-maker-modal">
        <DialogHeader className="p-5 pb-0">
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Wand2 className="w-5 h-5 text-[#D4AF37]" /> Token Animation Maker</DialogTitle>
          <DialogDescription className="text-[#f1e0c5]/50 text-xs">
            Build a 2D sprite-sheet animated token — every clip is optional. GM Pass required.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="px-5 pb-5">
          <TabsList className="grid grid-cols-2 bg-black/30">
            <TabsTrigger data-testid="animation-maker-tab-create" value="create">Create New</TabsTrigger>
            <TabsTrigger data-testid="animation-maker-tab-library" value="library">My Library ({library.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="create" className="space-y-5 max-h-[65vh] overflow-y-auto scrollbar-thin pr-1 pt-3">
            <Input
              data-testid="animation-maker-name-input"
              placeholder="Animation name (e.g. Goblin Raider)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-black/30 border-white/10"
            />

            {OPTIONAL_CLIPS.map((clip) => (
              <div key={clip} className="bg-black/20 border border-white/10 rounded-xl p-3 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    data-testid={`animation-maker-enable-${clip}`}
                    checked={!!enabled[clip]}
                    onCheckedChange={(v) => setEnabled((e) => ({ ...e, [clip]: !!v }))}
                  />
                  <span className="text-sm font-medium">{CLIP_META[clip].label}</span>
                  <span className="text-[10px] text-[#f1e0c5]/30">
                    {CLIP_FRAME_COUNTS[clip]} frames · {CLIP_META[clip].loop.replace("_", " ")}
                  </span>
                </label>
                {enabled[clip] && (
                  <div className="flex flex-wrap gap-3 pt-1">
                    {Array.from({ length: CLIP_FRAME_COUNTS[clip] }).map((_, i) => (
                      <FrameCropEditor
                        key={i}
                        index={i}
                        guidePose={CLIP_POSES[clip][i]?.pose}
                        guideLabel={CLIP_POSES[clip][i]?.label}
                        confirmedUrl={frames[clip]?.[i]?.url}
                        onConfirm={(blob, url) => setFrame(clip, i, blob, url)}
                        testId={`animation-maker-frame-${clip}-${i}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="bg-black/20 border border-white/10 rounded-xl p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium">Attack Variants</span>
                  <p className="text-[10px] text-[#f1e0c5]/30">Add one for each way this token attacks — tag it Melee / Magic / Bow / Throwing (pick as many as apply, or none for a generic Attack).</p>
                </div>
                <Button data-testid="animation-maker-add-attack-variant" size="sm" variant="secondary" className="rounded-full gap-1 text-xs shrink-0" onClick={addAttackBlock}>
                  <Plus className="w-3.5 h-3.5" /> Add
                </Button>
              </div>
              {attackBlocks.map((blk, bi) => (
                <div key={blk.id} className="bg-black/20 border border-white/10 rounded-lg p-3 space-y-2" data-testid={`animation-maker-attack-block-${bi}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-3">
                      {ATTACK_TYPES.map((t) => (
                        <label key={t.key} className="flex items-center gap-1.5 cursor-pointer">
                          <Checkbox
                            data-testid={`animation-maker-attack-type-${bi}-${t.key}`}
                            checked={blk.types.includes(t.key)}
                            onCheckedChange={() => toggleAttackType(blk.id, t.key)}
                          />
                          <span className="text-xs">{t.label}</span>
                        </label>
                      ))}
                    </div>
                    <button data-testid={`animation-maker-remove-attack-block-${bi}`} onClick={() => removeAttackBlock(blk.id)} className="text-[#f1e0c5]/40 hover:text-[#D90429]">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {!blk.types.length && <p className="text-[9px] text-[#f1e0c5]/30 italic">No type checked — saved as a generic "Attack".</p>}
                  <div className="flex flex-wrap gap-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <FrameCropEditor
                        key={i}
                        index={i}
                        guidePose={CLIP_POSES.attack[i]?.pose}
                        guideLabel={CLIP_POSES.attack[i]?.label}
                        confirmedUrl={blk.frames[i]?.url}
                        onConfirm={(blob, url) => setAttackFrame(blk.id, i, blob, url)}
                        testId={`animation-maker-attack-frame-${bi}-${i}`}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <Button data-testid="animation-maker-save-button" onClick={save} disabled={saving} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f]">
              {saving ? "Saving…" : applyToTokenId ? "Save & Apply to This Token" : "Save Animation"}
            </Button>
          </TabsContent>

          <TabsContent value="library" className="space-y-3 max-h-[65vh] overflow-y-auto scrollbar-thin pr-1 pt-3">
            {library.length === 0 && <p className="text-xs text-[#f1e0c5]/30 italic">No animations saved yet — build one on the Create New tab.</p>}
            {library.map((anim) => {
              const clipKeys = Object.keys(anim.clips || {});
              return (
                <div key={anim.id} className="bg-black/20 border border-white/10 rounded-xl p-3 space-y-2" data-testid={`animation-library-item-${anim.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm flex items-center gap-1.5">
                      {anim.name}
                      {anim.is_starter && <span data-testid={`animation-library-starter-badge-${anim.id}`} className="text-[8px] uppercase px-1.5 py-0.5 rounded-full bg-[#4d7c0f]/25 text-[#4d7c0f] font-bold">Starter Pack</span>}
                    </span>
                    {!anim.is_starter && (
                      <button data-testid={`animation-library-delete-${anim.id}`} onClick={() => deleteAnimation(anim.id)} className="text-[#f1e0c5]/40 hover:text-[#D90429]">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {clipKeys.map((k) => (
                      <span key={k} className="text-[9px] uppercase px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37]">{k.replace("_", " · ")}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    {applyToTokenId && (
                      <Button data-testid={`animation-library-apply-${anim.id}`} size="sm" className="rounded-full text-xs bg-[#4d7c0f] hover:bg-[#3d6208] gap-1" onClick={() => applyExisting(anim.id)}>
                        <Sparkles className="w-3 h-3" /> Apply
                      </Button>
                    )}
                    <Select value={downloadClip[anim.id] || ""} onValueChange={(v) => setDownloadClip((d) => ({ ...d, [anim.id]: v }))}>
                      <SelectTrigger data-testid={`animation-library-clip-select-${anim.id}`} className="h-7 text-[10px] bg-black/30 border-white/10 w-32">
                        <SelectValue placeholder="Clip…" />
                      </SelectTrigger>
                      <SelectContent>
                        {clipKeys.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button data-testid={`animation-library-download-${anim.id}`} size="sm" variant="secondary" className="rounded-full text-xs gap-1" onClick={() => downloadGif(anim.id)}>
                      <Download className="w-3 h-3" /> GIF
                    </Button>
                    <Button data-testid={`animation-library-recolor-${anim.id}`} size="sm" variant="secondary" className="rounded-full text-xs gap-1" onClick={() => setRecolorTarget(anim)}>
                      <Palette className="w-3 h-3" /> Recolor
                    </Button>
                  </div>
                </div>
              );
            })}
          </TabsContent>
        </Tabs>

        <RecolorAnimationModal
          open={!!recolorTarget}
          onOpenChange={(v) => !v && setRecolorTarget(null)}
          anim={recolorTarget}
          onSaved={loadLibrary}
        />
      </DialogContent>
    </Dialog>
  );
}
